/**
 * MafiaLobby — Room code, player list, config controls, role preview
 *
 * Requirements: 1.1, 1.6, 1.7, 2.1–2.4, 2.6–2.8, 21.1, 21.2, 23.1, 23.2, 31.9
 */

import React, { useState } from 'react';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { LobbyPlayer, MafiaGameConfig, getRoleDistribution, ROLE_INFO } from '../types';
import { MafiaInstructions } from './MafiaInstructions';
import './MafiaLobby.css';

export interface MafiaLobbyProps {
  players: LobbyPlayer[];
  config: MafiaGameConfig;
  isHost: boolean;
  roomCode: string;
  onConfigChange: (config: MafiaGameConfig) => void;
  onStartGame: () => void;
  onReady: (ready: boolean) => void;
  onLeave: () => void;
  myPlayerId?: string;
  guestGamesRemaining?: number;
}

export const MafiaLobby: React.FC<MafiaLobbyProps> = ({
  players,
  config,
  isHost,
  roomCode,
  onConfigChange,
  onStartGame,
  onReady,
  onLeave,
  myPlayerId,
  guestGamesRemaining,
}) => {
  const [showInstructions, setShowInstructions] = useState(false);

  const maxPlayers = config.extendedMode ? 30 : 15;
  const canStart = isHost && players.length >= 5;
  const myPlayer = players.find((p) => p.id === myPlayerId);
  const isReady = myPlayer?.isReady ?? false;

  // Role distribution preview
  let rolePreview: { mafia: number; doctor: number; detective: number; villager: number } | null = null;
  if (players.length >= 5) {
    try {
      rolePreview = getRoleDistribution(players.length, config.extendedMode);
    } catch {
      rolePreview = null;
    }
  }

  const handleConfigField = <K extends keyof MafiaGameConfig>(field: K, value: MafiaGameConfig[K]) => {
    onConfigChange({ ...config, [field]: value });
  };

  return (
    <div className="mafia-lobby" role="region" aria-label="Game Lobby">
      <div className="mafia-lobby__header">
        <h2 className="mafia-lobby__title">🎭 Mafia Lobby</h2>
        <div className="mafia-lobby__player-count">
          {players.length}/{maxPlayers} Players
        </div>
      </div>

      <div className="mafia-lobby__room-code">
        <RoomCodeDisplay
          roomCode={roomCode}
          gameType="mafia"
          maxPlayers={maxPlayers}
          showCancelButton={false}
        />
      </div>

      {/* Player List */}
      <div className="mafia-lobby__players">
        <h3>Players</h3>
        <ul className="mafia-lobby__player-list" aria-label="Connected players">
          {players.map((player) => (
            <li key={player.id} className="mafia-lobby__player-item">
              <span>
                <span className="mafia-lobby__player-name">{player.name}</span>
                {player.isHost && <span className="mafia-lobby__player-host">👑 Host</span>}
              </span>
              <span
                className={`mafia-lobby__player-ready ${
                  player.isReady ? 'mafia-lobby__player-ready--yes' : 'mafia-lobby__player-ready--no'
                }`}
              >
                {player.isReady ? '✅ Ready' : '⏳ Waiting'}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Game Config (host only) */}
      {isHost && (
        <div className="mafia-lobby__config">
          <h3>⚙️ Game Settings</h3>

          <div className="mafia-lobby__config-row">
            <span className="mafia-lobby__config-label">Discussion Timer</span>
            <select
              className="mafia-lobby__config-select"
              value={config.discussionDuration}
              onChange={(e) => handleConfigField('discussionDuration', Number(e.target.value) as MafiaGameConfig['discussionDuration'])}
              aria-label="Discussion timer duration"
            >
              <option value={30}>30s</option>
              <option value={60}>60s</option>
              <option value={90}>90s</option>
              <option value={120}>120s</option>
            </select>
          </div>

          <div className="mafia-lobby__config-row">
            <span className="mafia-lobby__config-label">Voting Timer</span>
            <select
              className="mafia-lobby__config-select"
              value={config.votingDuration}
              onChange={(e) => handleConfigField('votingDuration', Number(e.target.value) as MafiaGameConfig['votingDuration'])}
              aria-label="Voting timer duration"
            >
              <option value={15}>15s</option>
              <option value={30}>30s</option>
              <option value={45}>45s</option>
            </select>
          </div>

          <div className="mafia-lobby__config-row">
            <span className="mafia-lobby__config-label">Night Timer</span>
            <select
              className="mafia-lobby__config-select"
              value={config.nightDuration}
              onChange={(e) => handleConfigField('nightDuration', Number(e.target.value) as MafiaGameConfig['nightDuration'])}
              aria-label="Night timer duration"
            >
              <option value={15}>15s</option>
              <option value={30}>30s</option>
              <option value={45}>45s</option>
            </select>
          </div>

          <div className="mafia-lobby__config-row">
            <span className="mafia-lobby__config-label">Extended Mode</span>
            <div className="mafia-lobby__config-toggle">
              <input
                type="checkbox"
                checked={config.extendedMode}
                onChange={(e) => handleConfigField('extendedMode', e.target.checked)}
                aria-label="Toggle Extended Mode"
              />
            </div>
          </div>
          {config.extendedMode && (
            <div className="mafia-lobby__extended-label">
              Extended Mode — up to 30 players
            </div>
          )}
        </div>
      )}

      {/* Role Distribution Preview */}
      {rolePreview && (
        <div className="mafia-lobby__role-preview">
          <h3>Role Distribution ({players.length} players)</h3>
          <div className="mafia-lobby__role-grid">
            <div className="mafia-lobby__role-count">{ROLE_INFO.mafia.icon} Mafia: {rolePreview.mafia}</div>
            <div className="mafia-lobby__role-count">{ROLE_INFO.doctor.icon} Doctor: {rolePreview.doctor}</div>
            <div className="mafia-lobby__role-count">{ROLE_INFO.detective.icon} Detective: {rolePreview.detective}</div>
            <div className="mafia-lobby__role-count">{ROLE_INFO.villager.icon} Villager: {rolePreview.villager}</div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mafia-lobby__actions">
        {canStart && (
          <button
            className="mafia-lobby__btn mafia-lobby__btn--primary"
            onClick={onStartGame}
            aria-label="Start the game"
          >
            🎮 Start Game
          </button>
        )}

        {!isHost && (
          <button
            className={`mafia-lobby__btn ${isReady ? 'mafia-lobby__btn--ready' : ''}`}
            onClick={() => onReady(!isReady)}
            aria-label={isReady ? 'Unready' : 'Ready up'}
          >
            {isReady ? '✅ Ready!' : '🙋 Ready Up'}
          </button>
        )}

        <button
          className="mafia-lobby__btn"
          onClick={() => setShowInstructions(true)}
          aria-label="View game rules"
        >
          📖 Rules
        </button>

        <button
          className="mafia-lobby__btn mafia-lobby__btn--danger"
          onClick={onLeave}
          aria-label="Leave the lobby"
        >
          Leave Lobby
        </button>
      </div>

      <MafiaInstructions isOpen={showInstructions} onClose={() => setShowInstructions(false)} />
    </div>
  );
};

export default MafiaLobby;
